import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Box,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ListIcon from '@mui/icons-material/List';
import AppsIcon from '@mui/icons-material/Apps';
import LabelIcon from '@mui/icons-material/Label';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { PatternLibraryElement } from '../../models/listingFlow';
import { arePathsEqual } from '../../utils/pathUtils';
import { getSubElements, getContainerType } from '../../context/EditorContext'; // Importiere getSubElements und getContainerType
import { tokens } from '../../theme/tokens';

const LINE_COLOR = 'rgba(0, 0, 0, 0.23)'; // Farbe für die Verbindungslinien, ähnlich wie Divider

const TreeItem = styled(ListItem)<{ depth: number; isSelected: boolean; isLastChild?: boolean; hasChildren?: boolean }>`
  padding-left: ${props => props.depth * 20 + 8}px; // Erhöhte Einrückung
  background-color: ${props => props.isSelected ? 'rgba(0, 159, 100, 0.1)' : 'transparent'};
  border-left: ${props => props.isSelected ? `3px solid ${tokens.brand.green}` : '3px solid transparent'};
  position: relative; // Für Pseudoelemente

  // Vertikale Linie von oben zum aktuellen Element
  &::before {
    content: '';
    position: absolute;
    left: ${props => props.depth * 20 - 10}px; // Positioniert auf der Höhe des Einzugs-Icons
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: ${LINE_COLOR};
    // Verstecke die Linie für das erste Element auf jeder Ebene, wenn es keine Geschwister darüber hat
    // oder wenn depth === 0 (oberste Ebene)
    display: ${props => props.depth === 0 ? 'none' : 'block'};
    height: ${props => props.isLastChild ? 'calc(50% - 0px)' : '100%'}; // Linie bis zur Mitte, wenn letztes Kind
  }

  // Horizontale Linie vom vertikalen Strich zum Inhalt (oder zum Aufklapp-Icon)
  // Diese Linie wird vor dem Aufklapp-Icon platziert
  .MuiIconButton-root:first-of-type::before {
    content: '';
    position: absolute;
    left: -10px; // Von der Mitte des Icons nach links
    top: 50%;
    width: 10px;
    height: 1px;
    background-color: ${LINE_COLOR};
    display: ${props => props.depth === 0 ? 'none' : 'block'};
  }

  // Spezielle Anpassung für den Platzhalter-Box, wenn keine Kinder da sind, um die Linie zu zeichnen
  .placeholder-for-line::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 50%;
    width: 10px;
    height: 1px;
    background-color: ${LINE_COLOR};
    display: ${props => props.depth === 0 ? 'none' : 'block'};
  }

  &:hover {
    background-color: ${props => props.isSelected ? 'rgba(0, 159, 100, 0.15)' : 'rgba(0, 159, 100, 0.05)'};
  }
`;

const TreeItemContent = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const TreeItemText = styled(ListItemText)`
  margin: 0;

  .MuiListItemText-primary {
    font-size: 0.875rem;
    color: ${tokens.text.primary};
    font-weight: ${props => props.primary === 'true' ? 'bold' : 'normal'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

interface ElementHierarchyTreeProps {
  elements: PatternLibraryElement[];
  selectedPath: number[];
  onSelectElement: (path: number[]) => void;
  onDrillDown: (path: number[]) => void;
  currentPath: number[];
}

// Hilfsfunktion zum Ermitteln des Icons basierend auf dem Elementtyp
const getElementIcon = (elementType: string) => {
  switch (elementType) {
    case 'TextUIElement':
      return <TextFieldsIcon fontSize="small" />;
    case 'BooleanUIElement':
      return <CheckBoxIcon fontSize="small" />;
    case 'SingleSelectionUIElement':
      return <RadioButtonCheckedIcon fontSize="small" />;
    case 'NumberUIElement':
      return <NumbersIcon fontSize="small" />;
    case 'DateUIElement':
      return <CalendarTodayIcon fontSize="small" />;
    case 'FileUIElement':
      return <AttachFileIcon fontSize="small" />;
    case 'GroupUIElement':
      return <ViewModuleIcon fontSize="small" />;
    case 'ArrayUIElement':
      return <ListIcon fontSize="small" />;
    case 'ChipGroupUIElement':
      return <LabelIcon fontSize="small" />;
    case 'CustomUIElement':
      return <AppsIcon fontSize="small" />;
    case 'CustomUIElement_SCANNER':
      return <ViewInArIcon fontSize="small" />;
    case 'CustomUIElement_ADDRESS':
      return <HomeIcon fontSize="small" />;
    case 'CustomUIElement_LOCATION':
      return <LocationOnIcon fontSize="small" />;
    case 'CustomUIElement_ADMIN_BOUNDARY':
      return <ApartmentIcon fontSize="small" />;
    default:
      return <ChevronRightIcon fontSize="small" />;
  }
};

/**
 * Prüft rekursiv, ob ein Element oder eines seiner Kinder dem Suchbegriff entspricht.
 */
const elementMatchesSearch = (element: PatternLibraryElement, searchTerm: string): boolean => {
  if (!searchTerm) return true;
  const lower = searchTerm.toLowerCase();

  // Prüfe Titel (de + en)
  const titleDe = element.element?.title?.de?.toLowerCase() || '';
  const titleEn = element.element?.title?.en?.toLowerCase() || '';
  const fieldName = (element.element as any)?.field_id?.field_name?.toLowerCase() || '';
  const patternType = element.element?.pattern_type?.toLowerCase() || '';

  if (titleDe.includes(lower) || titleEn.includes(lower) || fieldName.includes(lower) || patternType.includes(lower)) {
    return true;
  }

  // Prüfe rekursiv Kinder
  const children = getSubElements(element);
  return children.some(child => elementMatchesSearch(child, searchTerm));
};

/**
 * Prüft ob ein Element direkt (nicht über Kinder) dem Suchbegriff entspricht.
 */
const elementDirectlyMatches = (element: PatternLibraryElement, searchTerm: string): boolean => {
  if (!searchTerm) return false;
  const lower = searchTerm.toLowerCase();
  const titleDe = element.element?.title?.de?.toLowerCase() || '';
  const titleEn = element.element?.title?.en?.toLowerCase() || '';
  const fieldName = (element.element as any)?.field_id?.field_name?.toLowerCase() || '';
  const patternType = element.element?.pattern_type?.toLowerCase() || '';
  return titleDe.includes(lower) || titleEn.includes(lower) || fieldName.includes(lower) || patternType.includes(lower);
};

// Rekursive Komponente für Baumknoten
const TreeNode: React.FC<{
  element: PatternLibraryElement;
  path: number[];
  depth: number;
  selectedPath: number[];
  onSelectElement: (path: number[]) => void;
  onDrillDown: (path: number[]) => void;
  currentPath: number[];
  isLastChildInLevel: boolean;
  searchTerm: string;
}> = ({
  element,
  path,
  depth,
  selectedPath,
  onSelectElement,
  onDrillDown,
  currentPath,
  isLastChildInLevel,
  searchTerm
}) => {
  const [expanded, setExpanded] = useState(false);
  const [manuallyToggled, setManuallyToggled] = useState(false); // Track manual user interaction
  const isSelected = arePathsEqual(path, selectedPath);
  const isInCurrentPath = path.length <= currentPath.length &&
                          path.every((value, index) => value === currentPath[index]);
  // Genau dieser Container ist aktuell „geöffnet" (seine Kinder füllen die mittlere Spalte).
  // Drill-down ≠ Auswahl: das wird an der Drill-Schaltfläche sichtbar gemacht.
  const isCurrentContext = currentPath.length > 0 && arePathsEqual(path, currentPath);

  // Verwende die importierte getSubElements Funktion
  const children = React.useMemo(() => getSubElements(element), [element]);
  const hasActualChildren = children.length > 0;

  // Bestimme den Container-Typ des Elements
  const containerType = React.useMemo(() => getContainerType(element), [element]);
  const containerColor = containerType === 'array' ? tokens.brand.orange :
                         containerType === 'chipgroup' ? tokens.accentIndigo.main : tokens.brand.green;
  const containerColorDark = containerType === 'array' ? tokens.brand.orangePressed :
                             containerType === 'chipgroup' ? tokens.accentIndigo.dark : tokens.brand.greenPressed;

  // Bestimme, ob das Element eine Visibility Condition hat
  const hasVisibilityCondition = () => {
    // Sicherer Zugriff auf visibility_condition
    // Bei BooleanUIElements aus ChipGroups fehlt manchmal die normale PatternLibraryElement-Struktur
    if (!element || !element.element) {
      return false;
    }

    return !!element.element.visibility_condition;
  };

  const isVisible = hasVisibilityCondition();

  // Bestimme den Anzeigenamen des Elements
  const getDisplayName = () => {
    if (!element || !element.element) {
      return `Element ${path[path.length - 1]}`;
    }

    // Für Subflow-Elemente ohne pattern_type, aber mit type
    if (!element.element.pattern_type && (element.element as any).type) {
      return (element.element as any).title?.de ||
             (element.element as any).title?.en ||
             (element.element as any).type ||
             `SubFlow ${path[path.length - 1]}`;
    }

    return element.element.title?.de ||
           element.element.title?.en ||
           `${element.element.pattern_type || 'Element'} ${path[path.length - 1]}`;
  };

  // Automatisch expandieren, wenn das Element im aktuellen Pfad ist (nur wenn nicht manuell geschlossen)
  React.useEffect(() => {
    if (isInCurrentPath && hasActualChildren && !manuallyToggled) {
      setExpanded(true);
    }
  }, [isInCurrentPath, hasActualChildren, manuallyToggled]);

  // Automatisch expandieren, wenn der Knoten Teil des selectedPath ist oder ein Vorfahre davon (nur wenn nicht manuell geschlossen)
  React.useEffect(() => {
    if (selectedPath && selectedPath.length > 0 && hasActualChildren && !expanded && !manuallyToggled) {
      const isNodeOrAncestorOfSelected =
        selectedPath.length >= path.length &&
        path.every((val, idx) => val === selectedPath[idx]);

      if (isNodeOrAncestorOfSelected) {
        setExpanded(true);
      }
    }
  }, [selectedPath, path, expanded, hasActualChildren, manuallyToggled]);

  // Reset manual toggle state when this element is no longer in the current path
  React.useEffect(() => {
    if (!isInCurrentPath) {
      setManuallyToggled(false);
    }
  }, [isInCurrentPath]);

  // Auto-expand wenn ein Kind den Suchbegriff trifft
  const hasSearchMatch = searchTerm ? elementMatchesSearch(element, searchTerm) : false;
  const isDirectMatch = searchTerm ? elementDirectlyMatches(element, searchTerm) : false;

  React.useEffect(() => {
    if (searchTerm && hasSearchMatch && hasActualChildren && !isDirectMatch) {
      // Expandiere, wenn ein Kind den Suchbegriff trifft
      setExpanded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, hasSearchMatch, hasActualChildren]);

  // Wenn Suchbegriff aktiv ist und dieses Element (oder Kinder) nicht passt, nicht rendern
  if (searchTerm && !hasSearchMatch) {
    return null;
  }

  return (
    <>
      <TreeItem
        depth={depth}
        isSelected={isSelected}
        isLastChild={isLastChildInLevel}
        hasChildren={hasActualChildren}
        onClick={() => onSelectElement(path)}
        aria-current={isSelected ? 'true' : undefined}
        sx={isDirectMatch ? { backgroundColor: 'rgba(25, 118, 210, 0.08)', fontWeight: 'bold' } : undefined}
      >
        <TreeItemContent>
          {hasActualChildren ? (
            <IconButton
              size="small"
              aria-label={expanded ? 'Unterelemente zuklappen' : 'Unterelemente aufklappen'}
              sx={{ position: 'relative' }} // Für ::before Pseudoelement
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
                setManuallyToggled(true); // Mark as manually toggled
              }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          ) : (
            // Platzhalter-Box, wenn keine Kinder, aber Linie benötigt wird
            <Box className="placeholder-for-line" sx={{ width: 28, position: 'relative' }} />
          )}

          <ListItemIcon sx={{ minWidth: 36 }}>
            {isVisible ? (
              <Badge
                color="primary"
                variant="dot"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {getElementIcon(element?.element?.pattern_type || 'default')}
              </Badge>
            ) : (
              getElementIcon(element?.element?.pattern_type || 'default')
            )}
          </ListItemIcon>

          <TreeItemText
            primary={`${getDisplayName()}${hasActualChildren ? ` (${containerType})` : ''}`}
            slotProps={{
              primary: {
                noWrap: true,
                title: `${getDisplayName()}${hasActualChildren ? ` (${containerType})` : ''}` // Tooltip bei Überlauf
              }
            }}
          />

          {hasActualChildren && ( // Verwende hasActualChildren
            <Tooltip title={isCurrentContext
              ? 'Geöffnet — der Inhalt erscheint in der mittleren Spalte'
              : (containerType === 'group' ? 'Gruppe öffnen' :
                 containerType === 'array' ? 'Array öffnen' :
                 containerType === 'chipgroup' ? 'Chips anzeigen' :
                 containerType === 'custom' ? 'Custom-Element öffnen' :
                 containerType === 'subflow' ? 'Subflow öffnen' :
                 'Unterelemente anzeigen')}>
              <IconButton
                size="small"
                aria-label={isCurrentContext
                  ? `${getDisplayName()}: geöffnet`
                  : `${getDisplayName()} öffnen — Inhalt in der mittleren Spalte anzeigen`}
                aria-pressed={isCurrentContext}
                onClick={(e) => {
                  e.stopPropagation();
                  onDrillDown(path);
                }}
                sx={{
                  ml: 'auto',
                  color: isCurrentContext ? tokens.surface.paper : containerColor,
                  ...(isCurrentContext ? {
                    bgcolor: containerColor,
                    '&:hover': { bgcolor: containerColorDark },
                  } : {}),
                }}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isVisible && (
            <Tooltip title="Hat Sichtbarkeitsregel">
              <VisibilityIcon
                fontSize="small"
                color="primary"
                sx={{ ml: hasActualChildren ? 0 : 'auto', opacity: 0.7 }} // Verwende hasActualChildren
              />
            </Tooltip>
          )}
        </TreeItemContent>
      </TreeItem>

      {hasActualChildren && ( // Verwende hasActualChildren
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child: PatternLibraryElement, index: number) => ( // Verwende children
              <TreeNode
                key={child.element.uuid || index}
                element={child}
                path={[...path, index]}
                depth={depth + 1}
                selectedPath={selectedPath}
                onSelectElement={onSelectElement}
                onDrillDown={onDrillDown}
                currentPath={currentPath}
                isLastChildInLevel={index === children.length - 1}
                searchTerm={searchTerm}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const ElementHierarchyTree: React.FC<ElementHierarchyTreeProps> = ({
  elements,
  selectedPath,
  onSelectElement,
  onDrillDown,
  currentPath
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const matchCount = useMemo(() => {
    if (!searchTerm) return 0;
    return elements.filter(el => elementMatchesSearch(el, searchTerm)).length;
  }, [elements, searchTerm]);

  return (
    <Box>
      {/* Suchfeld */}
      <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Element suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} aria-label="Suche leeren">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.8rem'
            }
          }}
        />
        {searchTerm && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {matchCount === 0 ? 'Keine Treffer' : `${matchCount} Treffer`}
          </Typography>
        )}
      </Box>

      <List component="nav" aria-label="element hierarchy" dense sx={{ p: 0 }}>
        {elements.map((element, index) => (
          <TreeNode
            key={element.element.uuid || index}
            element={element}
            path={[index]}
            depth={0}
            selectedPath={selectedPath}
            onSelectElement={onSelectElement}
            onDrillDown={onDrillDown}
            currentPath={currentPath}
            isLastChildInLevel={index === elements.length - 1}
            searchTerm={searchTerm}
          />
        ))}
      </List>
    </Box>
  );
};

export default ElementHierarchyTree;
